import React, { useEffect } from 'react';
import { useSection } from '@/app/providers';

interface SectionMultiSelectProps {
  /** Secciones seleccionadas. Vacío = recurso de todo el residencial. */
  value: string[];
  onChange: (sectionIds: string[]) => void;
  /** Nombre del recurso para el texto de ayuda ("amenidad", "comunicado"...). */
  resourceLabel?: string;
  /** Sustituye el texto de ayuda para el conjunto vacío. */
  emptyHint?: string;
  idPrefix: string;
  isDisabled?: boolean;
}

/**
 * Alcance multi-sección de un recurso (§6.5): un comunicado, encuesta,
 * documento, proyecto o amenidad puede pertenecer a varias secciones a la vez.
 *
 * No se renderiza si el tenant no tiene secciones, igual que SectionSelector.
 *
 * Un SECTION_ADMIN recibe del backend un catálogo ya acotado a sus secciones,
 * así que solo puede marcar esas; y como el backend prohíbe el conjunto vacío
 * (= global) a un admin acotado, aquí se preselecciona su sección activa y no
 * puede desmarcar la última.
 */
export const SectionMultiSelect: React.FC<SectionMultiSelectProps> = ({
  value,
  onChange,
  resourceLabel = 'recurso',
  emptyHint,
  idPrefix,
  isDisabled,
}) => {
  const { sections, hasSections, isSectionAdmin, selectedSectionId } = useSection();

  useEffect(() => {
    if (isSectionAdmin && value.length === 0 && selectedSectionId) onChange([selectedSectionId]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSectionAdmin, value.length, selectedSectionId]);

  if (!hasSections) return null;

  return (
    <div className="space-y-2">
      <p className="text-xs text-slate-500">
        {isSectionAdmin
          ? sections.length <= 1
            ? `Solo puedes publicar en tu sección.`
            : `Solo puedes publicar en tus secciones; al menos una.`
          : emptyHint ?? `Sin secciones seleccionadas = ${resourceLabel} para todo el residencial.`}
      </p>
      <div className="space-y-2">
        {sections.map((section) => {
          const checked = value.includes(section.id);
          const toggle = () =>
            onChange(
              checked ? value.filter((id) => id !== section.id) : [...value, section.id]
            );
          const id = `${idPrefix}-section-${section.id}`;
          return (
            <div
              key={section.id}
              className="flex items-center gap-2 p-2.5 border border-slate-200 rounded-lg"
            >
              <input
                type="checkbox"
                id={id}
                checked={checked}
                onChange={toggle}
                disabled={isDisabled || (isSectionAdmin && checked && value.length === 1)}
                className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500 shrink-0"
              />
              <label
                htmlFor={id}
                className="text-sm text-slate-700 cursor-pointer select-none"
              >
                {section.name}
              </label>
            </div>
          );
        })}
      </div>
    </div>
  );
};
